/**
 * ProfileDisplay Component
 * 
 * Read-only display of user profile with lock status indicators.
 * 
 * Features:
 * - Card-based layout matching dashboard style
 * - Lock icons for immutable fields
 * - Grouped sections (Personal Info, DOB, Address)
 * - Edit button to switch to form mode
 * - Responsive design
 * 
 * Created: 2025-01-17 (Phase 4.8: User Profile Management)
 * Phase: Phase 4.8 - Frontend Implementation
 */

import React from 'react';

const ProfileDisplay = ({ profile, onEdit }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Not set';
    
    // Format as (XXX) XXX-XXXX
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const getStateName = (stateCode) => {
    const states = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
      'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
      'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
      'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
      'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
      'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
      'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
      'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'Washington, D.C.'
    };
    return states[stateCode] || stateCode;
  };

  return (
    <div className="space-y-6">
      {/* Personal Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
          {profile?.is_name_locked && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Name Locked
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
            <div className="flex items-center">
              <p className="text-base text-gray-900 font-medium">
                {profile?.first_name} {profile?.last_name}
              </p>
              {profile?.is_name_locked && (
                <svg className="w-4 h-4 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              )}
            </div>
            {profile?.is_name_locked && (
              <p className="text-xs text-gray-500 mt-1">Locked at signup</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
            <p className="text-base text-gray-900">{profile?.email || 'Not set'}</p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
            <p className="text-base text-gray-900">{formatPhone(profile?.phone)}</p>
          </div>

          {/* Tier */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Account Tier</label>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              profile?.tier === 'premium' 
                ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}>
              {profile?.tier ? profile.tier.charAt(0).toUpperCase() + profile.tier.slice(1) : 'Free'}
            </span>
          </div>
        </div>
      </div>

      {/* Date of Birth Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Date of Birth</h3>
          {profile?.is_dob_locked && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Locked
            </span>
          )}
        </div>
        
        <div className="flex items-center">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-500 mb-1">Birth Date</label>
            <p className="text-base text-gray-900 font-medium">{formatDate(profile?.date_of_birth)}</p>
            {profile?.is_dob_locked && (
              <p className="text-xs text-gray-500 mt-1">Cannot be changed once set</p>
            )}
          </div>
          {profile?.is_dob_locked && (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          )}
        </div>
      </div>

      {/* Address Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
        
        <div className="space-y-3">
          {/* Street Address */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Street Address</label>
            <p className="text-base text-gray-900">
              {profile?.address_line_1 || 'Not set'}
              {profile?.address_line_2 && `, ${profile.address_line_2}`}
            </p>
          </div>

          {/* City, State, ZIP */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">City, State, ZIP</label>
            <p className="text-base text-gray-900">
              {profile?.city && profile?.state && profile?.zip_code
                ? `${profile.city}, ${getStateName(profile.state)} ${profile.zip_code}`
                : 'Not set'}
            </p>
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
            <p className="text-base text-gray-900">{profile?.country || 'United States'}</p>
          </div>
        </div>
      </div>

      {/* Profile Completion Status */}
      {profile?.profile_completed && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-800">Profile Complete</p>
              <p className="text-xs text-green-600 mt-0.5">
                Completed on {formatDate(profile?.profile_completed_at)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Button */}
      <div className="flex justify-end">
        <button
          onClick={onEdit}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-md font-medium hover:from-blue-700 hover:to-teal-700 transition shadow-md"
        >
          Edit Profile
        </button>
      </div>
    </div>
  );
};

export default ProfileDisplay;
